"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemoizedFn } from "ahooks";

import type { FinancialConnection } from "@/src/features/api/memberApi";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";

import {
  fetchPlaidConnections,
  filterConnectionsForInvestPortfolio,
} from "./memberPlaidApi";

export const PLAID_CONNECTIONS_QUERY_KEY = [
  "plaid-financial-connections",
] as const;

export interface UsePlaidConnectionsResult {
  connections: FinancialConnection[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  invalidate: () => Promise<void>;
}

export const usePlaidConnections = (): UsePlaidConnectionsResult => {
  const { memberApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PLAID_CONNECTIONS_QUERY_KEY,
    enabled: Boolean(memberApi),
    queryFn: async () => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }
      return fetchPlaidConnections(memberApi);
    },
  });

  const invalidate = useMemoizedFn(async () => {
    await queryClient.invalidateQueries({
      queryKey: PLAID_CONNECTIONS_QUERY_KEY,
    });
  });

  return {
    connections: filterConnectionsForInvestPortfolio(query.data ?? []),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
    invalidate,
  };
};
